import React from 'react';
import { TableRow } from '@material-ui/core';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles';
import Security from '../../commons/security/Security.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';

/**
 * VEC-3317 — una fila de la grilla de Solicitudes de Stock.
 *
 * EL GRANO ES LA LÍNEA, NO LA SOLICITUD. Un pedido de tres repuestos son tres filas, cada una con su
 * ítem y su cantidad, porque el Bodeguero despacha ítem por ítem. Las tres comparten ticket, fecha,
 * depósito y solicitante: eso es esperado y no un dato duplicado.
 *
 * LAS ACCIONES (VEC-3318) SOLO APARECEN SI SE PUEDEN USAR: cada una se gatea por su permiso y por
 * el estado de la línea. No se muestran deshabilitadas — un `button` deshabilitado no dispara el
 * tooltip de MUI, así que sería un icono mudo repetido en cada fila; es el motivo por el que se
 * sacaron los placeholders que tenía esta grilla antes de existir las acciones.
 */
const InventarioSolicitudesGridRow = (props) => {
	const row = props.row;
	const item = row.item || {};

	/** Las cantidades son ENVASES: enteros. El decimal vive en el factor de la unidad de medida. */
	const formatEntero = (value) => {
		if (value === null || value === undefined || value === '') return '-';
		return LocaleFormat.formatNumber(Number(value), 0);
	};

	const nombreUbicacion = (u) => (u && u.nombre ? u.nombre : '-');

	// Resalta lo que todavía falta entregar. En la pestaña de finalizadas siempre es 0, así que no
	// pinta nada.
	const tienePendiente = Number(row.cantidadPendiente) > 0;

	// Si el depósito tiene con qué cubrir lo que falta. Solo tiene sentido con algo pendiente: en una
	// línea cerrada no hay nada que cubrir, y pintarla de verde sugeriría una acción que ya no existe.
	const alcanzaElStock = tienePendiente && Number(row.disponible) >= Number(row.cantidadPendiente);

	/**
	 * Si hay algo que contar sobre cómo se gestionó esta línea.
	 *
	 * Se deduce de la fila y no se pregunta al backend: una línea CERRADA llegó ahí por alguna
	 * gestión, y una abierta con algo ya entregado tuvo al menos un despacho. El resto —una pendiente
	 * que nadie tocó— no tiene historial, y el botón abriría un modal vacío.
	 */
	const tieneHistorial = !tienePendiente || Number(row.cantidadDespachada) > 0;

	// El mapa que arma `useColumnVisibility`. Se tolera su ausencia para que la fila siga siendo
	// usable desde un test o un storybook sin montar el hook: sin el mapa, se ve todo.
	const visibles = props.visibles || {};
	const ve = (name) => visibles[name] !== false;

	return (
		<TableRow>
			{/* Selección masiva. Va SIEMPRE, no se puede ocultar por el selector de columnas. */}
			<StyledTableCell align="center" style={{ padding: '12px 8px' }}>
				<input
					type="checkbox"
					className="customCheckbox"
					checked={!!props.seleccionado}
					onChange={() => props.onSeleccionar(row.id)}
					aria-label={item.descripcion || String(row.id)}
				/>
			</StyledTableCell>
			{/* El id de la LÍNEA: identifica esta fila y solo esta. */}
			<StyledTableCell align="left" show={ve('id')}>{row.id}</StyledTableCell>
			<StyledTableCell align="left" show={ve('fecha')}>
				{row.fecha ? LocaleFormat.formatDate(row.fecha) : '-'}
			</StyledTableCell>
			{/* El ticket se muestra como número: en VecFleet el id ES el número de ticket. */}
			<StyledTableCell align="left" show={ve('ticket')}>
				{row.ticket && row.ticket.id ? row.ticket.id : '-'}
			</StyledTableCell>
			<StyledTableCell align="left" show={ve('numeroDeParte')}>{item.numeroDeParte || '-'}</StyledTableCell>
			<StyledTableCell align="left" show={ve('descripcion')}>{item.descripcion || '-'}</StyledTableCell>
			<StyledTableCell align="left" show={ve('deposito')}>{nombreUbicacion(row.ubicacion)}</StyledTableCell>
			{/* Las cantidades van CENTRADAS y no alineadas a la derecha: son enteros chicos que se leen
			    de un vistazo, y a la derecha quedaban pegados al borde de su columna, lejos del
			    encabezado que las nombra. La alineación derecha sirve para comparar magnitudes en una
			    columna de importes, que no es el caso. */}
			{/* En verde cuando alcanza para lo que falta entregar: es la lectura que se busca de un
			    vistazo —"¿puedo despachar esto sin pedirle nada a otro depósito?"—. En cero va en gris
			    y no en rojo: no hay nada malo en que un depósito no tenga stock, es el caso normal de
			    una solicitud que se resuelve por compra. */}
			<StyledTableCell align="center" show={ve('disponible')}>
				<span className={alcanzaElStock ? 'text-success' : 'text-muted'} style={{ fontWeight: 600 }}>
					{formatEntero(row.disponible)}
				</span>
			</StyledTableCell>
			<StyledTableCell align="center" show={ve('cantidad')}>{formatEntero(row.cantidad)}</StyledTableCell>
			<StyledTableCell align="center" show={ve('despachado')}>{formatEntero(row.cantidadDespachada)}</StyledTableCell>
			<StyledTableCell align="center" show={ve('pendiente')}>
				<span className={tienePendiente ? 'text-danger' : ''} style={{ fontWeight: 600 }}>
					{formatEntero(row.cantidadPendiente)}
				</span>
			</StyledTableCell>
			{/* Con hora: dos cierres del mismo día son lo normal, y la fecha sola no los distingue. */}
			<StyledTableCell align="center" show={ve('fechaCierre')}>
				{row.fechaCierre ? LocaleFormat.formatDateTime(row.fechaCierre) : '-'}
			</StyledTableCell>
			<StyledTableCell align="left" show={ve('solicitante')}>{row.solicitante || '-'}</StyledTableCell>
			<StyledTableCell align="center">
				<div className="d-inline-flex">
					{/* Solo con algo pendiente: una línea finalizada o cancelada no se gestiona, y el
					    backend la rechaza con 400. Mostrar el botón igual sería prometer algo que no
					    va a pasar. */}
					{tienePendiente && Security.hasPermission('INVENTARIO_SOLICITUDES_DESPACHAR') && (
						<StyledTooltip title={props.labelDespachar} arrow placement="top">
							{/* `aria-label` además del tooltip: el tooltip de MUI solo anuncia mientras está
							    abierto, así que un botón de puro ícono queda SIN NOMBRE para un lector de
							    pantalla y sin forma de ubicarlo por teclado. */}
							<button
								className="action btn btn-sm btn-icon btn-dt-grid text-success"
								aria-label={props.labelDespachar}
								onClick={() => props.onDespachar(row)}
							>
								<i className="fa fa-truck fa-xs" />
							</button>
						</StyledTooltip>
					)}
					{/* La lupa va PRIMERO: es la única acción que no escribe nada, y en Finalizadas
					    —donde las otras dos no aparecen— es la única que hay. */}
					{tieneHistorial && (
						<StyledTooltip title={props.labelGestiones} arrow placement="top">
							<button
								className="action btn btn-sm btn-icon btn-dt-grid text-success"
								aria-label={props.labelGestiones}
								onClick={() => props.onVerGestiones(row)}
							>
								<i className="fa fa-search fa-xs" />
							</button>
						</StyledTooltip>
					)}
					{tienePendiente && Security.hasPermission('INVENTARIO_SOLICITUDES_CANCELAR') && (
						<StyledTooltip title={props.labelCancelar} arrow placement="top">
							<button
								className="action btn btn-sm btn-icon btn-dt-grid text-danger"
								aria-label={props.labelCancelar}
								onClick={() => props.onCancelar(row)}
							>
								{/* Manito hacia abajo, como el Rechazar de la grilla de Facturas
								    (FacturasGrid.js:559): la acción no anula la solicitud, la RECHAZA —
								    el bodeguero dice que no la va a entregar—. El `fa-ban` de antes se
								    leía como "anular", que es otra cosa. */}
								<i className="fa fa-thumbs-down fa-xs" />
							</button>
						</StyledTooltip>
					)}
				</div>
			</StyledTableCell>
		</TableRow>
	);
};

export default InventarioSolicitudesGridRow;
